"use client";
import React from "react";
import { H3 } from "../headers";
import { FloatInput } from "../ui/input";
import { Button } from "../ui/button";
import { Controller, useForm } from "react-hook-form";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import config from "@/lib/config";
import { AppDispatch } from "@/redux/store";
import { useDispatch } from "react-redux";
import { login } from "@/redux/slices/login.slice";
import CheckboxItem from "../checkbox-item";
import { DrawerTitle } from "../ui/drawer";
import { filterObjectRemoveKeys } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Link from "next/link";
import CircularProgress from "../CircularProgress";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "sonner";

type Props = {
  onAfterSubmit: () => void;
  data: {
    mobile: string;
    otp: string;
  };
};

const CompleteProfile = ({ data, onAfterSubmit }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const t = useTranslations();

  const { handleSubmit, formState, control } = useForm({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      terms: undefined,
    },
    resolver: yupResolver(
      yup.object({
        firstName: yup
          .string()
          .required(t("form.requiredFirstName"))
          .matches(/^[a-zA-Zأ-ي]+$/, t("form.invalidFirstName")),
        lastName: yup
          .string()
          .required(t("form.requiredLastName"))
          .matches(/^[a-zA-Zأ-ي]+$/, t("form.invalidLastName")),
        email: yup.string().email(t("form.invalidEmail")).optional(),
        terms: yup.bool().isTrue().required(),
      })
    ),
  });

  const onSubmit = async (values: {
    firstName: string;
    lastName: string;
    email?: string;
    terms: boolean;
  }) => {
    try {
      const res = await ehjzleeInstance.post(
        config.user,
        filterObjectRemoveKeys({ ...data, ...values }, ["terms"])
      );
      dispatch(login(res.data));
      onAfterSubmit();
    } catch (err) {
      console.log(err);
      toast.error(t("form.failedAccount"));
    }
  };

  return (
    <>
      <DrawerTitle> {t("completeProfileSection.personalInfo")}</DrawerTitle>
      <H3 className="max-sm:text-center">
        {t("completeProfileSection.completeProfile")}{" "}
      </H3>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-6 mb-3 sm:mt-14 sm:mb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4 gap-2">
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <FloatInput
                {...field}
                placeholder={t("completeProfileSection.firstName")}
                required
                charactersOnly
                hasError={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field, fieldState }) => (
              <FloatInput
                {...field}
                placeholder={t("completeProfileSection.lastName")}
                required
                charactersOnly
                hasError={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <FloatInput
                {...field}
                type="email"
                containerClassName="col-span-2"
                placeholder={t("completeProfileSection.email")}
                hasError={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        <Button
          disabled={!formState.isValid || formState.isSubmitting}
          className="mt-8 w-full"
        >
          {formState.isSubmitting ? (
            <CircularProgress />
          ) : (
            t("completeProfileSection.createAccount")
          )}
        </Button>
        <div className="flex items-center gap-2 sm:mt-6 mt-3">
          <CheckboxItem
            label={
              <>
                {t("completeProfileSection.agreeToTerms")}
                <Link
                  href={"/terms-and-conditions"}
                  className="text-green-main hover:underline"
                  prefetch
                  target="_blank"
                >
                  {t("completeProfileSection.terms")}
                </Link>
              </>
            }
            name="terms"
            control={control}
          />
        </div>
      </form>
    </>
  );
};

export default CompleteProfile;
